import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import CONSTANTS, {
  GENERATE_ENUMS,
  INSURANCE_COMPANIES_FORM_ID,
  INSURANCE_COMPANY_EMPLOYEE_FORM_ID,
  USER_GROUPS,
} from "../constants/constants";
import { fetchRuntimeFormData } from "../services/FormService";
import {
  MenuItem,
  FormControl,
  TextField,
  Autocomplete,
  Button,
} from "@mui/material";
import { fetchUsersWithGroup } from "../services/UserService";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { generateQCR, generateRFQ } from "../services/ProposalService";
import { useDispatch, useSelector } from "react-redux";
import { pushNotification } from "../redux/actions";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import html2pdf from "html2pdf.js";
import { styled, lighten, darken } from "@mui/system";

const useStyles = makeStyles((theme) => ({
  generateContainer: {
    width: "65vw",
  },
  generateFormContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "2vh",
    justifyContent: "center",
    alignItems: "center",
  },
  formFlexRow: {
    display: "flex",
    flexDirection: "row",
    gap: "2%",
  },
  formFieldFlexRow: {
    display: "flex",
    flexDirection: "column",
    gap: "2%",
  },
  fieldAutoComplete: {
    minWidth: "30vw",
  },
  fieldDate: {
    minWidth: "25vw",
  },
}));

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  top: "-8px",
  padding: "4px 10px",

  color: theme.palette.primary.main,
  backgroundColor:
    theme.palette.mode === "light"
      ? lighten(theme.palette.primary.light, 0.85)
      : darken(theme.palette.primary.main, 0.8),
}));

const GroupItems = styled("ul")({
  padding: 0,
});

const GenerateRFQQCR = ({ generate, setOpenGenerate, contentRef }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { proposalDataRedux } = useSelector((state: any) => ({
    proposalDataRedux: state.proposalData,
  }));

  const [insuranceProviders, setInsuranceProviders] = useState<any>([]);
  const [selectedInsuranceProviders, setSelectedInsuranceProviders] =
    useState<any>([]);
  const [selectedUsers, setSelectedUsers] = useState<any>([]);
  const [userData, setUserData] = useState<any>([
    {
      userId: "",
      fullName: "",
    },
  ]);

  const [queriesNeedByDate, setQueriesNeedByDate] = useState<Dayjs | null>(
    null
  );
  const [quotesRequiredByDate, setQuotesRequiredByDate] =
    useState<Dayjs | null>(null);

  const [disableGenerate, setDisableGenerate] = useState(false);

  const getInsuranceCompanies = async () => {
    const response = await fetchRuntimeFormData(
      INSURANCE_COMPANY_EMPLOYEE_FORM_ID,
      0,
      10000
    );

    const filteredCompanies = response?.data?.content?.map((item) => {
      return item?.formData;
    });

    setInsuranceProviders(filteredCompanies || []);
  };

  const handleGetUsers = async () => {
    const response = await fetchUsersWithGroup(
      USER_GROUPS.UNDERWRITER.replace("/", "")
    );
    if (response.success) {
      setUserData(
        response?.data || [
          {
            userId: "",
            fullName: "",
          },
        ]
      );
    } else {
      setUserData([
        {
          userId: "",
          fullName: "",
        },
      ]);
    }
  };

  const handleSubmitGenerateRFQAndQCR = async (contentRef) => {
    setDisableGenerate(true);

    if (generate === GENERATE_ENUMS.GENERATE_RFQ) {
      const content = contentRef.current; // Access the content using the ref

      // Configuration for html2pdf
      const opt = {
        margin: 10,
        filename: `RFQ-${proposalDataRedux?.id}(${proposalDataRedux?.formData?.productTitle})`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a3", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      // Generate PDF from the content
      const pdfBlob = await html2pdf().from(content).set(opt).outputPdf("blob");

      const underWriterString = selectedUsers
        ?.map((user) => {
          return user?.userId;
        })
        .join(",");
      const insuranceCompaniesString = selectedInsuranceProviders
        ?.map((item) => {
          return item?.employeeEMailId;
        })
        .join(",");

      // const blob = new Blob([pdfBlob]);
      const fileOfBlob = new File([pdfBlob], `RFQ.pdf`, {
        type: "application/pdf",
      });
      const params = {
        file: fileOfBlob,
        productTitle: proposalDataRedux?.formData?.productTitle,
        queriesNeedBy: dayjs(queriesNeedByDate).format("DD/MM/YYYY"),
        quotesRequiredBy: dayjs(quotesRequiredByDate).format("DD/MM/YYYY"),
        underwriters: underWriterString,
        insuranceCompanies: insuranceCompaniesString,
      };

      const response = await generateRFQ(params);
      if (response?.success) {
        setDisableGenerate(false);
        setOpenGenerate(false);
        dispatch(
          pushNotification({
            isOpen: true,
            type: CONSTANTS.SUCCESS,
            message: response?.message,
          })
        );
      } else {
        setDisableGenerate(false);
        setOpenGenerate(false);
        dispatch(
          pushNotification({
            isOpen: true,
            type: CONSTANTS.ERROR,
            message: response?.message,
          })
        );
      }
    } else if (generate === GENERATE_ENUMS.GENERATE_QCR) {
      const content = contentRef.current; // Access the content using the ref

      // Configuration for html2pdf
      const opt = {
        margin: 10,
        filename: `QCR-${proposalDataRedux?.id}(${proposalDataRedux?.formData?.productTitle})`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a3", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      // Generate PDF from the content
      const pdfBlob = await html2pdf().from(content).set(opt).outputPdf("blob");

      const underWriterString = selectedUsers
        ?.map((user) => {
          return user?.userId;
        })
        .join(",");
      const insuranceCompaniesString = selectedInsuranceProviders
        ?.map((item) => {
          return item?.companyId;
        })
        .join(",");

      const fileOfBlob = new File([pdfBlob], `QCR.pdf`, {
        type: "application/pdf",
      });
      const params = {
        file: fileOfBlob,
        productTitle: proposalDataRedux?.formData?.productTitle,
        queriesNeedBy: dayjs(queriesNeedByDate).format("DD/MM/YYYY"),
        quotesRequiredBy: dayjs(quotesRequiredByDate).format("DD/MM/YYYY"),
        underwriters: underWriterString,
        insuranceCompanies: insuranceCompaniesString,
      };

      const response = await generateQCR(params);
      if (response?.success) {
        setDisableGenerate(false);
        setOpenGenerate(false);
        dispatch(
          pushNotification({
            isOpen: true,
            type: CONSTANTS.SUCCESS,
            message: response?.message,
          })
        );
      } else {
        setDisableGenerate(false);
        setOpenGenerate(false);
        dispatch(
          pushNotification({
            isOpen: true,
            type: CONSTANTS.ERROR,
            message: response?.message,
          })
        );
      }
    }
  };

  useEffect(() => {
    getInsuranceCompanies();
    handleGetUsers();
  }, []);

  return (
    <>
      <DialogContent dividers>
        <div className={classes.generateContainer}>
          <div className={classes.generateFormContainer}>
            <div className={classes.formFlexRow}>
              {generate === GENERATE_ENUMS.GENERATE_RFQ ? (
                <div className={classes.formFieldFlexRow}>
                  <label>Insurance Companies</label>
                  <FormControl>
                    <Autocomplete
                      className={classes.fieldAutoComplete}
                      multiple
                      disableClearable
                      id="insurance-providers-autocomplete"
                      options={insuranceProviders}
                      getOptionLabel={(option: any) =>
                        `${option.employeeName} - ${
                          option.insuranceCompanyName || ""
                        }` || ""
                      }
                      value={selectedInsuranceProviders}
                      groupBy={(option: any) => option?.insuranceCompanyName}
                      onChange={(_, newValue) =>
                        setSelectedInsuranceProviders(newValue)
                      }
                      renderInput={(params) => (
                        <TextField {...params} size="small" />
                      )}
                      renderGroup={(params) => (
                        <li key={params.key}>
                          <GroupHeader>{params.group}</GroupHeader>
                          <GroupItems>{params.children}</GroupItems>
                        </li>
                      )}
                    />
                  </FormControl>

                  {/* <Autocomplete
                  className={classes.fieldAutoComplete}
                  fullWidth
                  multiple
                  disableClearable
                  id="insurance-providers-autocomplete"
                  options={insuranceProviders}
                  value={selectedInsuranceProviders}
                  getOptionLabel={(option) => option?.companyId || ""}
                  onChange={(_, newValue) =>
                    setSelectedInsuranceProviders(newValue)
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem {...props} value={option}>
                      {option?.companyId}
                    </MenuItem>
                  )}
                /> */}
                </div>
              ) : (
                <div className={classes.formFlexRow}></div>
              )}
              <div className={classes.formFieldFlexRow}>
                <label>Underwriters</label>
                <Autocomplete
                  className={classes.fieldAutoComplete}
                  multiple
                  disableClearable
                  id="underwriters-field"
                  options={userData}
                  getOptionLabel={(option: any) => option?.fullName || ""}
                  value={selectedUsers}
                  onChange={(_, newValue: any) => setSelectedUsers(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} size="small" />
                  )}
                  renderOption={(props, option) => (
                    <MenuItem {...props} value={option}>
                      {option?.fullName}
                    </MenuItem>
                  )}
                />
              </div>
            </div>
            {generate === GENERATE_ENUMS.GENERATE_RFQ && (
              <div className={classes.formFlexRow}>
                <div className={classes.formFieldFlexRow}>
                  <label>Queries Need By</label>
                  <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        value={queriesNeedByDate}
                        onChange={(newValue) => {
                          setQueriesNeedByDate(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              placeholder: "DD/MM/YYYY",
                              className: classes.fieldDate,
                            }}
                            size="small"
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </div>
                </div>
                <div className={classes.formFieldFlexRow}>
                  <label>Quotes Required By</label>
                  <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        value={quotesRequiredByDate}
                        onChange={(newValue) => {
                          setQuotesRequiredByDate(newValue);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputProps={{
                              ...params.inputProps,
                              placeholder: "DD/MM/YYYY",
                              className: classes.fieldDate,
                            }}
                            size="small"
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          autoFocus
          color="primary"
          variant="contained"
          onClick={() => handleSubmitGenerateRFQAndQCR(contentRef)}
          disabled={disableGenerate}
        >
          Send Email
        </Button>
      </DialogActions>
    </>
  );
};

export default GenerateRFQQCR;
